<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCampaignProgress } from './hooks/useCampaignProgress.js';
import {
  campaignStatusLabel,
  normalizeCampaignStatus,
  isFinalCampaignStatus,
  CAMPAIGN_STATUS,
} from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';
import Progress from 'dashboard/components-shadcn/new-york/ui/progress/Progress.vue';

const props = defineProps({
  campaignId: { type: Number, required: true },
  schedulingId: { type: Number, required: true },
  initialStatus: { type: String, default: 'scheduled' },
  channelType: { type: String, default: '' },
  statusData: { type: Object, default: null },
  progressPercentage: { type: Number, default: 0 },
  totalItems: { type: Number, default: 0 },
  statusMessage: { type: String, default: '' },
  holdStats: { type: Object, default: null },
  // Botão "Detalhes" (abre o modal via CustomEvent global) — contrato do
  // fluxo LEGADO (WhatsApp App). A página nova de agendamentos desliga: o
  // detalhe vive na aba Estatísticas do ScheduleDrawer.
  showDetails: { type: Boolean, default: true },
});
const isStarting = ref(false);
const isStoping = ref(false);

const { t } = useI18n();

const {
  getProgressForScheduling,
  startCampaign,
  stopCampaign,
  loadPersistedData,
} = useCampaignProgress();

const campaignData = getProgressForScheduling(props.schedulingId);

if (props.statusData || props.progressPercentage > 0) {
  loadPersistedData(props.schedulingId);
}

// Status atual \u2014 enum CRU. Fonte: WebSocket > statusData (banco) > initialStatus.
// Default 'scheduled' apenas quando n\u00e3o h\u00e1 nenhuma fonte.
const currentStatus = computed(() => {
  const raw =
    campaignData.value.status ||
    props.statusData?.status ||
    props.initialStatus;
  return normalizeCampaignStatus(raw) || CAMPAIGN_STATUS.SCHEDULED;
});

// R\u00f3tulo traduzido pra exibi\u00e7\u00e3o (i18n do frontend; status desconhecido volta cru)
const statusLabel = computed(() => campaignStatusLabel(currentStatus.value, t));

// Configuracao visual do status (chaveada pelo enum CRU)
const statusConfig = computed(() => {
  const configs = {
    [CAMPAIGN_STATUS.SCHEDULED]: {
      icon: 'i-lucide-calendar',
      class: 'text-n-blue-11 bg-n-blue-3',
    },
    [CAMPAIGN_STATUS.RUNNING]: {
      icon: 'i-lucide-loader',
      class: 'text-n-amber-11 bg-n-amber-3',
      animate: true,
    },
    [CAMPAIGN_STATUS.CANCELLED]: {
      icon: 'i-lucide-x-octagon',
      class: 'text-n-amber-11 bg-n-amber-3',
    },
    [CAMPAIGN_STATUS.COMPLETED]: {
      icon: 'i-lucide-check-circle',
      class: 'text-n-teal-11 bg-n-teal-3',
    },
    [CAMPAIGN_STATUS.FAILED]: {
      icon: 'i-lucide-alert-triangle',
      class: 'text-n-ruby-11 bg-n-ruby-3',
    },
  };
  return configs[currentStatus.value] || null;
});

// Dados de progresso
// IMPORTANTE: statistics SEMPRE vem do banco (props.statusData), nunca do WebSocket
const progressData = computed(() => {
  const wsData = campaignData.value;
  const statusFinished = isFinalCampaignStatus(currentStatus.value);

  // Statistics: prioridade para props (banco), depois WebSocket. SEM
  // fabricar sintético quando falta — inventar valid_for_sending = total
  // bruto zerava as filtragens e inflava o "enviadas" derivado dele.
  const statistics = props.statusData?.statistics || wsData.statistics || null;

  if (statusFinished) {
    // Números REAIS do backend (WebSocket mais recente, senão banco) — nada
    // derivado. A versão anterior calculava sent = válidos − falhas e
    // forçava 100% no concluído, mostrando "tudo enviado" em campanha
    // cortada (limite diário, cancel, holds) enquanto a página de entregas
    // mostrava o ledger real.
    const hasWsData =
      wsData.totalItems > 0 ||
      wsData.currentItem > 0 ||
      wsData.progress > 0 ||
      wsData.itemsFailed > 0;

    const sent = hasWsData
      ? wsData.currentItem || 0
      : (props.statusData?.items_sent ?? 0);

    const failed = hasWsData
      ? wsData.itemsFailed || 0
      : (props.statusData?.items_failed ?? 0);

    const total = hasWsData
      ? wsData.totalItems ||
        statistics?.valid_for_sending ||
        props.totalItems ||
        0
      : statistics?.valid_for_sending ||
        props.totalItems ||
        props.statusData?.total_items ||
        0;

    const percentage = Math.round(
      hasWsData ? wsData.progress || 0 : props.progressPercentage || 0
    );

    return {
      sent,
      total,
      percentage,
      failed,
      statistics,
      message:
        wsData.message ||
        props.statusMessage ||
        props.statusData?.message ||
        '',
    };
  }

  if (
    currentStatus.value === CAMPAIGN_STATUS.RUNNING ||
    wsData.status === CAMPAIGN_STATUS.RUNNING
  ) {
    if (
      wsData.totalItems > 0 ||
      wsData.currentItem > 0 ||
      wsData.progress > 0
    ) {
      return {
        sent: wsData.currentItem || 0,
        total: wsData.totalItems || 0,
        percentage: Math.round(wsData.progress || 0),
        failed: wsData.itemsFailed || 0,
        statistics,
        message: wsData.message || '',
      };
    }
  }

  return {
    sent: props.statusData?.items_sent ?? 0,
    total: props.totalItems || props.statusData?.total_items || 0,
    percentage: Math.round(
      props.progressPercentage || props.statusData?.progress || 0
    ),
    failed: props.statusData?.items_failed ?? 0,
    statistics,
    message: props.statusMessage || props.statusData?.message || '',
  };
});

// Computed helpers
const hasStatistics = computed(() => {
  const stats = progressData.value.statistics;
  return stats && Object.keys(stats).length > 0;
});

const isFailed = computed(() => currentStatus.value === CAMPAIGN_STATUS.FAILED);

const isFinished = computed(() => isFinalCampaignStatus(currentStatus.value));

// Carimbo do ReconcileCountersJob: os contadores foram conferidos com o
// ledger de entregas (rejeições assíncronas da Meta já dobradas). Sem o
// carimbo, o número exibido é o snapshot do disparo e ainda pode mudar.
const isReconciled = computed(() =>
  Boolean(progressData.value.statistics?.counters_reconciled_at)
);
const isRunning = computed(
  () => currentStatus.value === CAMPAIGN_STATUS.RUNNING
);
const shouldShowControlButtons = computed(
  () => props.channelType !== 'Channel::Api'
);
const canStart = computed(
  () =>
    currentStatus.value === CAMPAIGN_STATUS.SCHEDULED &&
    !isStarting.value &&
    shouldShowControlButtons.value
);
const canStop = computed(
  () => isRunning.value && !isStoping.value && shouldShowControlButtons.value
);

// Texto de progresso compacto - mostra enviados/válidos (porcentagem%)
const progressText = computed(() => {
  const stats = progressData.value.statistics;
  const sent = progressData.value.sent;
  const validForSending =
    stats?.valid_for_sending || progressData.value.total || 0;

  if (validForSending === 0) return '';

  const percentage =
    validForSending > 0 ? Math.round((sent / validForSending) * 100) : 0;
  return `${sent}/${validForSending} (${percentage}%)`;
});

// Contadores da feature HOLD (entregas seguradas por atendimento ativo no
// disparo). Chips compactos aparecem SO quando ha algo > 0 — a maioria das
// campanhas nao segura nada, entao nao poluimos a celula.
const held = computed(() => props.holdStats?.held || 0);
const released = computed(() => props.holdStats?.released || 0);
const expired = computed(() => props.holdStats?.expired || 0);
const skipped = computed(() => props.holdStats?.skipped || 0);
const hasHoldStats = computed(
  () =>
    held.value > 0 ||
    released.value > 0 ||
    expired.value > 0 ||
    skipped.value > 0
);

// Handlers
// Mantemos isStarting/isStoping=true ate a transicao real de status acontecer
// (watch abaixo libera quando sai de "Agendado" / "Em execucao").
// Fallback de 30s pra cobrir caso o WebSocket atrase/falhe.
// Em erro, libera imediatamente pra user tentar de novo.
const FALLBACK_LOCK_MS = 30_000;

// Reset do lock quando o status muda — evita botao preso em loading apos
// campanhas curtas (ex: 1 record que conclui em <1s, antes do timeout disparar).
watch(currentStatus, (newStatus, oldStatus) => {
  if (newStatus === oldStatus) return;
  if (isStarting.value && newStatus !== CAMPAIGN_STATUS.SCHEDULED) {
    isStarting.value = false;
  }
  if (isStoping.value && newStatus !== CAMPAIGN_STATUS.RUNNING) {
    isStoping.value = false;
  }
});

const handleStart = async () => {
  if (isStarting.value) return;
  isStarting.value = true;
  try {
    await startCampaign(props.campaignId, props.schedulingId);
    setTimeout(() => {
      isStarting.value = false;
    }, FALLBACK_LOCK_MS);
  } catch {
    isStarting.value = false;
  }
};

const handleStop = async () => {
  if (isStoping.value) return;
  isStoping.value = true;
  try {
    await stopCampaign(props.campaignId, props.schedulingId);
    setTimeout(() => {
      isStoping.value = false;
    }, FALLBACK_LOCK_MS);
  } catch {
    isStoping.value = false;
  }
};

const openStatsModal = () => {
  // Emitir evento para abrir modal no nivel da tabela
  window.dispatchEvent(
    new CustomEvent('open-campaign-stats-modal', {
      detail: {
        campaignId: props.campaignId,
        schedulingId: props.schedulingId,
        statistics: progressData.value.statistics,
        progressData: progressData.value,
        holdStats: props.holdStats,
        status: currentStatus.value,
        statusMessage:
          props.statusMessage ||
          progressData.value?.message ||
          progressData.value?.statusMessage ||
          '',
      },
    })
  );
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Status Row -->
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <!-- Status Badge — status desconhecido cai em estilo neutro exibindo o
             valor cru (nunca esconde atrás de um loader) -->
        <span
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
          :class="
            statusConfig ? statusConfig.class : 'text-n-slate-11 bg-n-slate-3'
          "
        >
          <span
            v-if="statusConfig"
            class="size-3.5"
            :class="[
              statusConfig.icon,
              statusConfig.animate ? 'animate-spin' : '',
            ]"
          />
          {{ statusLabel }}
        </span>

        <!-- Progress Text (quando finalizado) -->
        <template v-if="isFinished">
          <!-- Campanha concluída SEM dados -->
          <template v-if="progressData.total === 0">
            <span class="text-xs font-medium text-n-slate-10 italic">
              {{ $t('CAMPAIGN.STATISTICS.NO_RECORDS') }}
            </span>
            <!-- Botão Detalhes - mostra se tem statistics, ou se falhou (para ver error logs) -->
            <button
              v-if="showDetails && (hasStatistics || isFailed)"
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md text-n-blue-11 hover:text-n-blue-12 hover:bg-n-blue-3 transition-colors"
              @click="openStatsModal"
            >
              <span class="i-lucide-info size-3" />
              {{ $t('CAMPAIGN.STATISTICS.DETAILS') }}
            </button>
          </template>

          <!-- Campanha concluída COM dados -->
          <template v-else>
            <span class="text-xs font-medium text-n-slate-11">
              {{ progressText }}
            </span>

            <!-- Ver Detalhes Button - mostra quando tem statistics, falhas, progresso parcial, ou falhou -->
            <button
              v-if="
                showDetails &&
                (hasStatistics ||
                  isFailed ||
                  progressData.failed > 0 ||
                  progressData.sent > 0)
              "
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md text-n-blue-11 hover:text-n-blue-12 hover:bg-n-blue-3 transition-colors"
              @click="openStatsModal"
            >
              <span class="i-lucide-info size-3" />
              {{ $t('CAMPAIGN.STATISTICS.DETAILS') }}
            </button>
          </template>

          <!-- Consolidado: contadores conferidos com o ledger de entregas
               (rejeições da Meta pós-envio já refletidas no número) -->
          <span
            v-if="isReconciled"
            v-tooltip="t('CAMPAIGN.STATISTICS.RECONCILED_TOOLTIP')"
            class="i-lucide-badge-check size-3.5 text-n-teal-11"
          />
        </template>
      </div>

      <!-- Control Buttons -->
      <div v-if="shouldShowControlButtons" class="flex items-center gap-1">
        <!-- Start Button -->
        <button
          v-if="canStart || isStarting"
          :disabled="isStarting"
          class="flex items-center justify-center size-7 rounded-md transition-all text-white"
          :class="
            isStarting
              ? 'bg-n-teal-8 cursor-not-allowed'
              : 'bg-n-teal-9 hover:bg-n-teal-10'
          "
          @click="handleStart"
        >
          <span
            v-if="isStarting"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-play size-3.5" />
        </button>

        <!-- Stop Button -->
        <button
          v-if="canStop || isStoping"
          :disabled="isStoping"
          class="flex items-center justify-center size-7 rounded-md transition-all text-white"
          :class="
            isStoping
              ? 'bg-n-ruby-8 cursor-not-allowed'
              : 'bg-n-ruby-9 hover:bg-n-ruby-10'
          "
          @click="handleStop"
        >
          <span
            v-if="isStoping"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-square size-3.5" />
        </button>
      </div>
    </div>

    <!-- Chips HOLD (entregas seguradas) — cada um so aparece quando > 0 -->
    <div v-if="hasHoldStats" class="flex items-center flex-wrap gap-1">
      <span
        v-if="held > 0"
        v-tooltip="t('CAMPAIGN.STATISTICS.HOLD.HELD_TOOLTIP')"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-n-amber-3 text-n-amber-11"
      >
        <span class="i-lucide-pause size-3" />
        {{ held }}
      </span>
      <span
        v-if="released > 0"
        v-tooltip="t('CAMPAIGN.STATISTICS.HOLD.RELEASED_TOOLTIP')"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-lucide-play size-3" />
        {{ released }}
      </span>
      <span
        v-if="expired > 0"
        v-tooltip="t('CAMPAIGN.STATISTICS.HOLD.EXPIRED_TOOLTIP')"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-n-ruby-3 text-n-ruby-11"
      >
        <span class="i-lucide-hourglass size-3" />
        {{ expired }}
      </span>
      <span
        v-if="skipped > 0"
        v-tooltip="t('CAMPAIGN.STATISTICS.HOLD.SKIPPED_TOOLTIP')"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-n-slate-3 text-n-slate-11"
      >
        <span class="i-lucide-ban size-3" />
        {{ skipped }}
      </span>
    </div>

    <!-- Progress Bar (quando em execucao) -->
    <div v-if="isRunning" class="space-y-1.5">
      <div class="flex items-center justify-between text-xs text-n-slate-11">
        <span class="font-medium">
          <template v-if="progressData.total === 0">
            {{ $t('CAMPAIGN.STATISTICS.STARTING') }}
          </template>
          <template v-else>
            {{ (progressData.sent + progressData.failed).toLocaleString() }} /
            {{ progressData.total.toLocaleString() }}
          </template>
        </span>
        <span class="font-bold text-sm text-n-blue-11">
          {{ progressData.percentage }}%
        </span>
      </div>

      <Progress
        :model-value="progressData.percentage"
        class="h-1.5 rounded-full"
      />
    </div>
  </div>
</template>
